<script setup lang="ts">

import {mergeProps, ref, watch} from "vue";
import VFieldLabel from "@/components/forms/VFieldLabel.vue";
import DeleteDialog from "@/components/dialogs/DeleteDialog.vue";
import {CapacityFramework} from "@/types";
import {useAuth} from "@/composables/auth";

const {user} = useAuth();

interface Props {
    capacityFramework: CapacityFramework
    hidden?: boolean;
    hideEditorActions?: boolean;
    hideAdminActions?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    hidden: false,
    hideEditorActions: true,
    hideAdminActions: true
});

const emit = defineEmits<{
    (e: 'update'): void;
    (e: 'leave', userId: number): void;
    (e: 'delete'): void;
}>();

const confirmName = ref<string>('');

const isLeaveCapacityFrameworkDialogOpen = ref<boolean>(false);
const isDeleteCapacityFrameworkDialogOpen = ref<boolean>(false);

watch(isDeleteCapacityFrameworkDialogOpen, (value) => {
    if (value) {
        confirmName.value = '';
    }
});
</script>

<template>
    <v-menu
        v-if="!props.hidden"
        location="bottom end"
    >
        <template #activator="{ props: bind }">
            <v-tooltip
                location="top"
                content-class="bg-black border-sm text-tooltip"
                max-width="200"
            >
                <template #activator="{ props: subBind }">
                    <v-icon-btn
                        v-bind="mergeProps(bind, subBind)"
                        icon="mdi-dots-vertical"
                        size="32"
                        class="ml-2"
                        rounded="sm"
                    />
                </template>
                More actions
            </v-tooltip>
        </template>
        <v-list
            rounded="lg"
            border="sm"
            elevation="3"
            class="bg-background py-0 my-2"
            width="250"
        >
            <v-list-item
                v-if="!props.hideEditorActions"
                class="text-body-2 ma-1 pl-2"
                link
                density="compact"
                @click="emit('update')"
            >
                Edit framework
            </v-list-item>
            <v-divider />
            <v-list-item
                v-if="props.capacityFramework.user_id !== user.id"
                class="text-body-2 ma-1 pl-2 text-error"
                link
                density="compact"
                @click="isLeaveCapacityFrameworkDialogOpen = true"
            >
                Leave framework
            </v-list-item>
            <v-list-item
                v-if="!props.hideAdminActions"
                class="text-body-2 ma-1 pl-2 text-error"
                link
                density="compact"
                @click="isDeleteCapacityFrameworkDialogOpen = true"
            >
                Delete framework
            </v-list-item>
        </v-list>
    </v-menu>
    <delete-dialog
        v-model="isLeaveCapacityFrameworkDialogOpen"
        hide-title
        @submit="emit('leave', user.id)"
    >
        <template #default>
            Are you sure you want to leave the "{{ props.capacityFramework.name }}" framework?
        </template>
        <template #label>
            Leave framework
        </template>
    </delete-dialog>
    <delete-dialog
        v-model="isDeleteCapacityFrameworkDialogOpen"
        :disabled="confirmName !== props.capacityFramework.name"
        @submit="emit('delete')"
    >
        <template #title>
            Are you absolutely sure?
        </template>
        <template #default>
            <v-alert
                variant="tonal"
                color="red-lighten-3"
                class="mb-3"
                icon="mdi-alert-circle-outline"
                icon-size="20"
                density="compact"
            >
                <span class="text-black text-body-2 font-weight-regular">
                    This action cannot be undone. All assessments associated with this framework will be permanently deleted.
                </span>
            </v-alert>
            This action can lead to data loss. To prevent accidental actions we ask you to confirm your intention.
            <v-field-label class="mt-3">
                Enter the following to confirm:
            </v-field-label>
            <code class="bg-grey-lighten-3 pa-1 rounded">
                {{ props.capacityFramework.name }}
            </code>
            <v-text-field
                v-model="confirmName"
                name="confirmName"
                density="compact"
                class="mt-2"
                hide-details
            />
        </template>
        <template #label>
            Confirm
        </template>
    </delete-dialog>
</template>

<style scoped>
:deep(.v-list-item__prepend) {
    width: 42px;
}

.v-list-item--density-compact.v-list-item--one-line {
    min-height: 32px;
}
</style>
